import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MdDeleteOutline } from "react-icons/md";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";

interface BranchesDeleteModalProps {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  _id: string;
  isMain: boolean;
}

const BranchesDeleteModal: React.FC<BranchesDeleteModalProps> = ({
  setNeedRefetch,
  _id,
  isMain
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  useLockBodyScroll(isOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const handleDeleteBranch = async () => {
    if (isMain) return toast.error(t("branchCard.cannotDeleteMainBranch") as string);
    const LoadingToast = toast.loading(
      t("branchDeleteModal.loading") as string,
    );
    try {
      const url = `${BASE_URL}/api/dashboard/branches/${_id}?locale=${lang}`;
      const response = await axios.delete(url, {
        headers: {
          Authorization: token,
        },
      });

      if (!response.data.success) {
        toast.error(
          response.data.error || (t("branchDeleteModal.fail") as string),
        );
        return;
      }

      toast.success(
        t("branchDeleteModal.success") as string,
      );
      setNeedRefetch(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.error ||
          (t("branchDeleteModal.fail") as string);
        toast.error(errorMessage);
      } else {
        toast.error(
          t("branchDeleteModal.error") as string,
        );
      }
    } finally {
      toast.dismiss(LoadingToast);
    }
  };

  if (!isOpen) {
    return (
      <button
        className="flex  items-center justify-center"
        onClick={() => setIsOpen(true)}
      >
        <MdDeleteOutline className="text-white bg-error p-1 rounded-md max-[400px]:text-[32px] text-[29px]" />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        ref={modalRef}
        className={`max-w-sm rounded-lg bg-white p-6 shadow-lg ${lang === "ar" ? "text-right" : "text-left"
          }`}
        style={{ direction: lang === "ar" ? "rtl" : "ltr" }}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        <h2 className="mb-4 text-lg font-semibold text-gray-800">
          {t("branchDeleteModal.confirmMessage")}
        </h2>
        <div
          className={`flex ${lang === "ar" ? "justify-start space-x-reverse" : "justify-end"
            } space-x-4`}
        >
          <button
            onClick={() => setIsOpen(false)}
            className="rounded-lg bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
          >
            {t("branchDeleteModal.cancel")}
          </button>
          <button
            onClick={handleDeleteBranch}
            className="rounded-lg bg-red-500 px-4 py-2 text-white hover:bg-red-600"
          >
            {t("branchDeleteModal.delete")}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default BranchesDeleteModal;
